import { z } from 'zod';
import { REPAIR_STATUSES } from '../constants/repair-states.js';
import { moneyCentsSchema, uuidSchema } from './common.js';
/**
 * Day-only, 'yyyy-mm-dd'. Neither date on a ticket carries a time — nothing in
 * this shop schedules to the hour yet, only the day.
 */
export const repairDateSchema = z.iso.date();
export const repairListQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(500).default(50),
    q: z.string().trim().max(200).optional(),
    status: z.enum(REPAIR_STATUSES).optional(),
    assignedStaffId: uuidSchema.optional(),
    /** Together with `to`, the range the calendar is currently showing. */
    from: repairDateSchema.optional(),
    to: repairDateSchema.optional(),
    /**
     * Which date column `from`/`to` filter against. `either` matches a ticket
     * whose drop-off, promised, or work date falls in range — the calendar's
     * default, so a ticket does not vanish just because only one of its dates
     * lands in the visible month.
     */
    dateField: z.enum(['dropoff', 'promised', 'work', 'either']).default('either'),
    /** Only tickets with no drop-off, promised or work date — the ones the
     *  calendar has nowhere to draw, so they need a list of their own. */
    undated: z.stringbool().optional(),
    /** Leave out completed and cancelled tickets. */
    open: z.stringbool().optional(),
});
export const repairListItemSchema = z.object({
    id: uuidSchema,
    ticketNumber: z.string(),
    status: z.enum(REPAIR_STATUSES),
    customerId: uuidSchema,
    customerName: z.string().nullable(),
    customerPhone: z.string().nullable(),
    deviceDescription: z.string(),
    issue: z.string(),
    /** The day the customer said they would bring the device in. Set from
     *  checkout (or staff), and always known before the ticket is ever intake. */
    dropOffDate: repairDateSchema.nullable(),
    /** The pickup day promised to the customer, set once the device has been
     *  checked in. Null before then — see `awaiting_dropoff`. */
    promisedDate: repairDateSchema.nullable(),
    /** The day a technician is booked to work on it. Null until staff book one. */
    workDate: repairDateSchema.nullable(),
    assignedStaffId: uuidSchema.nullable(),
    assignedStaffName: z.string().nullable(),
    orderId: uuidSchema.nullable(),
    orderNumber: z.string().nullable(),
    quotedCents: z.number().int().nullable(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
});
export const repairDetailSchema = repairListItemSchema.extend({
    imei: z.string().nullable(),
    serializedUnitId: uuidSchema.nullable(),
    locationId: uuidSchema.nullable(),
});
export const repairEventSchema = z.object({
    id: uuidSchema,
    fromStatus: z.enum(REPAIR_STATUSES).nullable(),
    toStatus: z.enum(REPAIR_STATUSES),
    actorType: z.enum(['staff', 'customer', 'kiosk', 'system']),
    actorLabel: z.string().nullable(),
    note: z.string().nullable(),
    createdAt: z.iso.datetime(),
});
export const transitionRepairSchema = z.object({
    to: z.enum(REPAIR_STATUSES),
    note: z.string().trim().max(500).nullable().default(null),
});
/**
 * Every field optional, at least one required — same shape as
 * `updateStockRequestSchema`. `null` clears a date or an assignment; `undefined`
 * (the key left out) leaves it untouched.
 */
export const updateRepairSchema = z
    .object({
    dropOffDate: repairDateSchema.nullable(),
    promisedDate: repairDateSchema.nullable(),
    workDate: repairDateSchema.nullable(),
    assignedStaffId: uuidSchema.nullable(),
    quotedCents: moneyCentsSchema.nullable(),
    deviceDescription: z.string().trim().min(1).max(500),
    issue: z.string().trim().min(1).max(2000),
})
    .partial()
    .refine((v) => Object.keys(v).length > 0, { message: 'Nothing to update' });
/** t1237: where a repair part came from — see `repairTicketParts`. */
export const REPAIR_PART_SOURCES = ['stock', 'unit', 'other'];
export const repairPartSchema = z.object({
    id: uuidSchema,
    source: z.enum(REPAIR_PART_SOURCES),
    label: z.string(),
    amountCents: z.number().int(),
    productId: uuidSchema.nullable(),
    productName: z.string().nullable(),
    locationId: uuidSchema.nullable(),
    locationName: z.string().nullable(),
    qty: z.number().int().nullable(),
    unitId: uuidSchema.nullable(),
    /** The license number / ICCID of a tracked unit. */
    unitIdentifier: z.string().nullable(),
    vendor: z.string().nullable(),
    invoiceRef: z.string().nullable(),
    note: z.string().nullable(),
    actorLabel: z.string().nullable(),
    createdAt: z.iso.datetime(),
});
export const repairPartsResponseSchema = z.object({
    items: z.array(repairPartSchema),
    totalCents: z.number().int(),
});
const partNoteSchema = z.string().trim().max(500).nullish();
export const addRepairPartSchema = z.discriminatedUnion('source', [
    z.object({
        source: z.literal('stock'),
        productId: uuidSchema,
        locationId: uuidSchema,
        qty: z.number().int().min(1).max(10000),
        /** Total for the quantity; defaults to the product's cost × qty. */
        amountCents: moneyCentsSchema.nullish(),
        note: partNoteSchema,
    }),
    z.object({
        source: z.literal('unit'),
        unitId: uuidSchema,
        /** Defaults to the unit's (or product's) cost. */
        amountCents: moneyCentsSchema.nullish(),
        note: partNoteSchema,
    }),
    z.object({
        source: z.literal('other'),
        label: z.string().trim().min(1).max(200),
        amountCents: moneyCentsSchema,
        vendor: z.string().trim().max(200).nullish(),
        invoiceRef: z.string().trim().max(200).nullish(),
        note: partNoteSchema,
    }),
]);
/** t1365: one stretch of hourly labour logged on a repair. */
export const repairLabourEntrySchema = z.object({
    id: uuidSchema,
    staffId: uuidSchema.nullable(),
    staffName: z.string().nullable(),
    workDate: repairDateSchema.nullable(),
    minutes: z.number().int(),
    rateCents: z.number().int(),
    amountCents: z.number().int(),
    note: z.string().nullable(),
    /** Set once a checkout has charged this entry. */
    billedOrderId: uuidSchema.nullable(),
    billedOrderNumber: z.string().nullable(),
    actorLabel: z.string().nullable(),
    createdAt: z.iso.datetime(),
});
export const repairLabourResponseSchema = z.object({
    items: z.array(repairLabourEntrySchema),
    unbilledMinutes: z.number().int(),
    unbilledCents: z.number().int(),
    /** The shop's default hourly rate (Settings); 0 when none is set. */
    defaultRateCents: z.number().int(),
    /** The service item labour is rung up under; null until one is chosen in Settings. */
    labourProduct: z
        .object({ id: uuidSchema, name: z.string(), taxCategoryName: z.string().nullable() })
        .nullable(),
});
export const addRepairLabourSchema = z.object({
    minutes: z.number().int().min(1).max(10000),
    /** Per hour; the client prefills the shop default. */
    rateCents: moneyCentsSchema,
    /** The technician; defaults to the person logging it on the server side if omitted. */
    staffId: uuidSchema.nullish(),
    workDate: repairDateSchema.nullish(),
    note: z.string().trim().max(500).nullish(),
});
export const removeRepairPartQuerySchema = z.object({
    restock: z
        .union([z.boolean(), z.enum(['true', 'false'])])
        .transform((v) => (typeof v === 'boolean' ? v : v === 'true'))
        .default(true),
});
//# sourceMappingURL=repair.js.map